import { useCallback, useRef, useState } from 'react';
import { t } from '../lib/strings';

// Interactive before/after comparison slider. Both images are absolute `object-fit: cover`
// layers; the "before" image is clipped to the divider position, so dragging the handle
// wipes between the two. Self-contained (no deps), pointer + keyboard driven, Shadow-DOM safe.
// Renders only when both images are set.
//   variant 'demo'   → fixed 5:4 box (the logged-out info-modal showcase, matched pair).
//   variant 'result' → box takes the BEFORE (uploaded vehicle) aspect, measured at load, so
//                       the user's photo shows faithfully and the AI result covers the same
//                       box → both halves identical size and aligned.
export function BeforeAfter({
  before,
  after,
  variant = 'demo',
  beforeAlt,
  afterAlt,
}: {
  before: string;
  after: string;
  variant?: 'demo' | 'result';
  beforeAlt?: string;
  afterAlt?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const [pos, setPos] = useState(50); // % revealed of the "before" image (from the left)
  const [aspect, setAspect] = useState('4 / 3'); // result box ratio, from the vehicle image

  const setFromClientX = useCallback((clientX: number) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  }, []);

  if (!before || !after) return null;

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    setFromClientX(e.clientX);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragging.current) setFromClientX(e.clientX);
  };
  const onPointerUp = () => {
    dragging.current = false;
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      setPos((p) => Math.max(0, p - 5));
      e.preventDefault();
    } else if (e.key === 'ArrowRight') {
      setPos((p) => Math.min(100, p + 5));
      e.preventDefault();
    }
  };

  const natural = variant === 'result';

  return (
    <div
      ref={ref}
      className={`aiw-cmp${natural ? ' aiw-cmp-natural' : ''}`}
      style={natural ? { aspectRatio: aspect } : undefined}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <img
        className="aiw-cmp-img"
        src={after}
        alt={afterAlt ?? t('info_after_alt')}
        draggable={false}
      />
      <img
        className="aiw-cmp-img aiw-cmp-before"
        src={before}
        alt={beforeAlt ?? t('info_before_alt')}
        draggable={false}
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        onLoad={
          natural
            ? (e) =>
                setAspect(
                  `${e.currentTarget.naturalWidth} / ${e.currentTarget.naturalHeight}`,
                )
            : undefined
        }
      />
      <span className="aiw-cmp-label aiw-cmp-label-before">{t('info_before')}</span>
      <span className="aiw-cmp-label aiw-cmp-label-after">{t('info_after')}</span>
      <div className="aiw-cmp-divider" style={{ left: `${pos}%` }}>
        <button
          type="button"
          className="aiw-cmp-handle"
          role="slider"
          aria-label={t('info_compare')}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(pos)}
          tabIndex={0}
          onKeyDown={onKeyDown}
        >
          ↔
        </button>
      </div>
    </div>
  );
}
